/**
 * Times, shown in the shop's timezone.
 *
 * date-fns `format` renders using the device's clock and timezone. That is
 * fine on a desk, but the counter runs on POS terminals whose clocks drift —
 * one was three minutes out — and a cashier comparing the screen to the till
 * display has no way to know which is wrong.
 *
 * The timestamps themselves are sound: the server writes them to within a
 * second of real time. Only the rendering was at the mercy of the terminal.
 * Formatting explicitly in Asia/Beirut makes what is on screen depend on the
 * stored instant alone, so every terminal shows the same time for the same
 * event whatever its own clock says.
 */
const TZ = 'Asia/Beirut';

function parse(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** 14:47 */
export function shopTime(value) {
  const d = parse(value);
  if (!d) return '—';
  return d.toLocaleTimeString('en-GB', {
    timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

/** 16 Oct 14:47 */
export function shopDateTime(value) {
  const d = parse(value);
  if (!d) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: TZ, day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).replace(',', '');
}

/** 16 Oct 2026 */
export function shopDate(value) {
  const d = parse(value);
  if (!d) return '—';
  return d.toLocaleDateString('en-GB', {
    timeZone: TZ, day: 'numeric', month: 'short', year: 'numeric',
  });
}

/** 16 October 2027 — for an expiry, where the long form reads better. */
export function shopDateLong(value) {
  const d = parse(value);
  if (!d) return '—';
  return d.toLocaleDateString('en-GB', {
    timeZone: TZ, day: 'numeric', month: 'long', year: 'numeric',
  });
}

/**
 * How far this terminal's clock is from the server's, in seconds.
 *
 * Positive means the terminal is ahead. The server sends its own time on
 * every response; comparing it is what lets the page tell a cashier the
 * terminal is wrong, rather than leaving them to find out in a dispute.
 */
export function clockDrift(serverIso) {
  const d = parse(serverIso);
  if (!d) return null;
  return Math.round((Date.now() - d.getTime()) / 1000);
}
